import { assignGuardAfter, failureScope, type AssignPlanStudent } from "@edu/shared";
import { ApiError, ResponseFormatError, type ApiErrorCode } from "../../../lib/api.ts";

/**
 * Цикл мультивыдачи: по одному `POST /v1/homework` на ученика,
 * последовательно (1.3 docs/homework-phase2-design.md).
 *
 * Почему цикл, а не серверная пачка: у пачки собственный ключ идемпотентности,
 * собственный формат ответа, собственный потолок, и она всё равно обязана уметь
 * частичный отказ. Здесь ноль новых контрактов, прогресс по именам и
 * естественный повтор только неудавшихся. Условие пересмотра названо числом:
 * цикл из двенадцати создаваний дольше десяти секунд означает, что пачка нужна,
 * и меряется это событием телеметрии (5.12).
 *
 * Последовательно, а не параллельно: двенадцать одновременных запросов на канале
 * 128 кбит/с это не «в двенадцать раз быстрее», а очередь в одном узком месте
 * плюс лимит частоты, и прогресс по ним показать нечем.
 */

/** Что случилось с одним учеником. Группу отказа решает `failureScope`. */
export type AssignOutcome =
  | { kind: "created"; homeworkId: string }
  | { kind: "failed"; code: ApiErrorCode }
  | { kind: "network" };

export interface CycleReport {
  /**
   * Почему цикл кончился раньше списка:
   *
   * - `all` дело в задании, отказ повторился бы у всех до единого
   * - `network` две сетевые неудачи подряд
   * - `offline` браузер сказал, что связи нет, и следующий запрос не отправлялся
   */
  stopped: "all" | "network" | "offline" | null;
  /** Причина отказа «на всех»: показывается один раз, а не двенадцать. */
  allFailure: unknown;
  networkFailures: number;
}

/**
 * Группа отказа по исключению клиента.
 *
 * ⚠️ `ResponseFormatError` это «на всех», а не «связь пропала», и разница
 * важная: ответ пришёл, работа скорее всего создана, а разобрать его не смог
 * бандл. Повторять такое бессмысленно, а останавливаться обязательно, иначе
 * человек получит одиннадцать одинаковых непонятных строк вместо «обновите
 * страницу». Ключи идемпотентности при этом лежат в плане, значит повтор после
 * перезагрузки безопасен.
 */
function scopeOf(e: unknown): "all" | "one" | "network" {
  if (e instanceof ResponseFormatError) return "all";
  if (e instanceof ApiError) return failureScope(e.code);
  return "network";
}

export async function runAssignCycle(args: {
  targets: readonly AssignPlanStudent[];
  create: (student: AssignPlanStudent) => Promise<{ id: string }>;
  /** Ответ по одному ученику. Здесь план и пишется в хранилище. */
  onOutcome: (student: AssignPlanStudent, outcome: AssignOutcome) => void;
  isOnline: () => boolean;
}): Promise<CycleReport> {
  let networkStreak = 0;
  let networkFailures = 0;

  for (const student of args.targets) {
    /**
     * Проверка ДО отправки: `navigator.onLine === false` означает, что круг по
     * сети заведомо пропадёт зря, и двенадцать таймаутов подряд человек будет
     * ждать минуту. Неотправленные остаются `pending`, то есть «не отправлено»,
     * а это неизвестность, а не отказ.
     */
    if (!args.isOnline()) return { stopped: "offline", allFailure: null, networkFailures };

    try {
      const created = await args.create(student);
      args.onOutcome(student, { kind: "created", homeworkId: created.id });
      networkStreak = assignGuardAfter(networkStreak, "created").networkStreak;
    } catch (e) {
      const scope = scopeOf(e);
      if (scope === "all") {
        // Строку этому ученику не пишем и выбор не трогаем: дело не в нём
        return { stopped: "all", allFailure: e, networkFailures };
      }
      if (scope === "network") {
        networkFailures += 1;
        args.onOutcome(student, { kind: "network" });
      } else {
        args.onOutcome(student, {
          kind: "failed",
          code: e instanceof ApiError ? e.code : "unknown_error",
        });
      }
      const guard = assignGuardAfter(networkStreak, scope);
      networkStreak = guard.networkStreak;
      if (guard.stop) return { stopped: "network", allFailure: null, networkFailures };
    }
  }

  return { stopped: null, allFailure: null, networkFailures };
}
